import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { CrmViewEditor, type ViewDraft } from "./crm-view-editor";
import { FieldShell, NativeSelect, useConfirm } from "./crm-ui";
import { useCrmText } from "@/lib/crm/i18n";
import { entityLabel, fieldLabel, type CrmEntity } from "@/lib/crm/fields";
import {
  crmFetch, crmKeys, plural, useCrmList, useCrmViews,
  type CrmContext, type CrmRecord, type CrmView, type CrmViewInput, type CrmViewSort,
} from "@/lib/crm/api";
import { applyView, displayValue, recordTitle, viewDisplayName } from "@/lib/crm/view-engine";
import { queryClient } from "@/lib/queryClient";
import {
  ArrowDown, ArrowUp, Check, ChevronDown, Loader2, Plus, Power, PowerOff, RefreshCw, Search,
  Send, SlidersHorizontal, Target, Trash2, UserCheck, Users,
} from "lucide-react";

const PAGE_SIZE = 50;
const DEFAULT_WIDTH = 180;

const readViewId = (entity: CrmEntity) => {
  try { return localStorage.getItem(`fs:crm:view:${entity}`); } catch { return null; }
};
const writeViewId = (entity: CrmEntity, id: string) => {
  try { localStorage.setItem(`fs:crm:view:${entity}`, id); } catch { /* storage unavailable */ }
};

type EditorMode = "edit" | "saveAs" | "create";

/** Dynamics-style list: views (system, shared, personal), quick search, sortable/resizable columns, bulk commands. */
export function CrmGrid({ entity, ctx }: { entity: CrmEntity; ctx: CrmContext }) {
  const { lang, tx } = useCrmText();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [confirmDialog, ask] = useConfirm();
  const views = useCrmViews(entity);
  const list = useCrmList(entity);

  const [viewId, setViewId] = useState<string | null>(() => readViewId(entity));
  const [search, setSearch] = useState("");
  const [sortOverride, setSortOverride] = useState<CrmViewSort | null>(null);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [busy, setBusy] = useState(false);

  const allViews = views.data || [];
  const view: CrmView | undefined = allViews.find((v) => v.id === viewId) || allViews[0];

  // A view change starts clean: its own sort, widths, first page, no selection.
  useEffect(() => {
    setSortOverride(null);
    setWidths({});
    setSelection(new Set());
    setPage(0);
  }, [view?.id]);
  useEffect(() => { setPage(0); }, [search]);

  const rows = useMemo(() => {
    if (!view) return [];
    return applyView(entity, list.data || [], view, { userId: ctx.userId, search, lang, sort: sortOverride });
  }, [entity, list.data, view, ctx.userId, search, lang, sortOverride]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const selected = rows.filter((r) => selection.has(r.id));
  const sort = sortOverride || view?.sort;
  const dirty = !!sortOverride || Object.keys(widths).length > 0;
  const route = entity === "lead" ? "/leads" : "/contacts";

  const pickView = (id: string) => {
    setViewId(id);
    writeViewId(entity, id);
  };

  const toggleSort = (key: string) => {
    const current = sortOverride || view?.sort;
    setSortOverride(current?.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  // ── Column resize ────────────────────────────────────────────────────────
  const resizing = useRef<{ key: string; startX: number; startW: number } | null>(null);
  const thRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const startResize = (key: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: thRefs.current[key]?.getBoundingClientRect().width || DEFAULT_WIDTH };
    const move = (ev: MouseEvent) => {
      const r = resizing.current;
      if (!r) return;
      const w = Math.min(600, Math.max(60, Math.round(r.startW + ev.clientX - r.startX)));
      setWidths((prev) => ({ ...prev, [r.key]: w }));
    };
    const up = () => {
      resizing.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const columns = (view?.columns || []).map((c) => ({ key: c.key, width: widths[c.key] ?? c.width ?? DEFAULT_WIDTH }));

  // ── Views ────────────────────────────────────────────────────────────────
  const currentDraft = (mode: EditorMode): ViewDraft => {
    if (!view || mode === "create") {
      const firstCols = view?.columns.slice(0, 1) || [{ key: "fullName" }];
      return { name: "", isShared: false, columns: firstCols.map((c) => ({ key: c.key })), filter: { state: "active", owner: "any", conditions: [] }, sort: { key: firstCols[0].key, dir: "asc" } };
    }
    const name = viewDisplayName(view, lang);
    return {
      name: mode === "saveAs" ? tx("copyOf", { name }) : name,
      isShared: mode === "saveAs" ? false : view.isShared,
      columns: view.columns.map((c) => ({ key: c.key, width: widths[c.key] ?? c.width ?? null })),
      filter: view.filter,
      sort: sort || view.sort,
    };
  };

  const reloadViews = () => queryClient.invalidateQueries({ queryKey: crmKeys.views(entity) });

  const saveDraft = async (mode: EditorMode, d: ViewDraft) => {
    const input: CrmViewInput = { entity, name: d.name, isShared: d.isShared, columns: d.columns, filter: d.filter, sort: d.sort };
    if (mode === "edit" && view) {
      if (view.isSystem) await crmFetch("PUT", `/api/crm/views/system/${entity}/${view.systemKey}`, input);
      else await crmFetch("PUT", `/api/crm/views/${view.id}`, input);
      await reloadViews();
    } else {
      const created = await crmFetch<CrmView>("POST", "/api/crm/views", input);
      await reloadViews();
      pickView(created.id);
    }
    setSortOverride(null);
    setWidths({});
    toast({ title: tx("viewSaved") });
  };

  const saveCurrent = async () => {
    try { await saveDraft("edit", currentDraft("edit")); }
    catch (e) { toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" }); }
  };

  const resetSystemView = async () => {
    if (!view?.systemKey) return;
    try {
      await crmFetch("DELETE", `/api/crm/views/system/${entity}/${view.systemKey}`);
      await reloadViews();
      toast({ title: tx("viewReset") });
    } catch (e) { toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" }); }
  };

  const deleteView = async () => {
    if (!view || view.isSystem) return;
    const ok = await ask({ title: tx("deleteView"), text: tx("confirmDeleteView", { name: view.name }), confirm: tx("delete"), cancel: tx("cancel"), destructive: true });
    if (!ok) return;
    try {
      await crmFetch("DELETE", `/api/crm/views/${view.id}`);
      pickView(allViews[0]?.id || "");
      await reloadViews();
      toast({ title: tx("viewDeleted") });
    } catch (e) { toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" }); }
  };

  // ── Bulk commands ────────────────────────────────────────────────────────
  const runBulk = async (items: CrmRecord[], call: (r: CrmRecord) => Promise<unknown>, okKey: "deletedMany" | "activatedMany" | "deactivatedMany" | "assignedMany") => {
    setBusy(true);
    let ok = 0, failed = 0;
    for (const r of items) {
      try { await call(r); ok++; } catch { failed++; }
    }
    setBusy(false);
    setSelection(new Set());
    await queryClient.invalidateQueries({ queryKey: crmKeys.list(entity) });
    if (ok) toast({ title: tx(okKey, { n: ok }) });
    if (failed) toast({ title: tx("partialFail", { n: failed }), variant: "destructive" });
  };

  const setState = (active: boolean) =>
    runBulk(selected, (r) => crmFetch("POST", `/api/crm/${plural(entity)}/${r.id}/state`, { active }), active ? "activatedMany" : "deactivatedMany");

  const removeSelected = async () => {
    const ok = await ask({
      title: tx("confirmDeleteTitle"),
      text: selected.length === 1 ? tx("confirmDeleteOne") : tx("confirmDeleteMany", { n: selected.length }),
      confirm: tx("delete"), cancel: tx("cancel"), destructive: true,
    });
    if (ok) await runBulk(selected, (r) => crmFetch("DELETE", `/api/crm/${plural(entity)}/${r.id}`), "deletedMany");
  };

  const assignSelected = async () => {
    if (!assignTo) return;
    setAssignOpen(false);
    await runBulk(selected, (r) => crmFetch("PUT", `/api/crm/${plural(entity)}/${r.id}`, { values: { ownerId: assignTo } }), "assignedMany");
  };

  const sendDocument = () => {
    const r = selected[0];
    if (r) navigate(`/documents/new?${entity === "lead" ? "leadId" : "contactId"}=${r.id}`);
  };

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selection.has(r.id));
  const toggleAll = () => setSelection((prev) => {
    const next = new Set(prev);
    if (allOnPageSelected) pageRows.forEach((r) => next.delete(r.id));
    else pageRows.forEach((r) => next.add(r.id));
    return next;
  });
  const toggleRow = (id: string) => setSelection((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const systemViews = allViews.filter((v) => v.isSystem);
  const myViews = allViews.filter((v) => !v.isSystem && v.isMine);
  const sharedViews = allViews.filter((v) => !v.isSystem && !v.isMine);
  const anyInactive = selected.some((r) => r.values.isActive === false);
  const anyActive = selected.some((r) => r.values.isActive !== false);
  const EntityIcon = entity === "lead" ? Target : Users;

  const viewItem = (v: CrmView) => (
    <DropdownMenuItem key={v.id} onClick={() => pickView(v.id)} className="flex items-center gap-2" data-testid={`view-option-${v.systemKey || v.id}`}>
      <Check className={`h-4 w-4 ${v.id === view?.id ? "opacity-100" : "opacity-0"}`} />
      <span className="min-w-0 flex-1 truncate">{viewDisplayName(v, lang)}</span>
      {v.overridden && <span className="text-[10px] uppercase text-muted-foreground">{tx("customizedTag")}</span>}
      {!v.isSystem && v.isShared && v.isMine && <span className="text-[10px] uppercase text-muted-foreground">{tx("sharedTag")}</span>}
      {!v.isSystem && !v.isMine && v.ownerName && <span className="max-w-[7rem] truncate text-[10px] text-muted-foreground">{v.ownerName}</span>}
    </DropdownMenuItem>
  );

  return (
    <div className="space-y-3" data-testid={`crm-grid-${entity}`}>
      {confirmDialog}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <EntityIcon className="h-6 w-6 shrink-0" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-xl font-bold hover-elevate sm:text-2xl" data-testid="view-selector">
                <span className="truncate">{view ? viewDisplayName(view, lang) : entityLabel(entity, lang, true)}</span>
                <ChevronDown className="h-5 w-5 shrink-0 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80">
              <DropdownMenuLabel className="text-xs text-muted-foreground">{tx("systemViews")}</DropdownMenuLabel>
              <DropdownMenuGroup>{systemViews.map(viewItem)}</DropdownMenuGroup>
              {myViews.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{tx("myViews")}</DropdownMenuLabel>
                  <DropdownMenuGroup>{myViews.map(viewItem)}</DropdownMenuGroup>
                </>
              )}
              {sharedViews.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{tx("sharedViews")}</DropdownMenuLabel>
                  <DropdownMenuGroup>{sharedViews.map(viewItem)}</DropdownMenuGroup>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEditor("create")} data-testid="view-create">
                <Plus className="mr-2 h-4 w-4" /> {tx("createView")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {dirty && <span className="hidden text-xs text-muted-foreground sm:inline">· {tx("viewChanged")}</span>}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("searchView")} className="pl-8" data-testid="grid-search" />
        </div>
      </div>

      {/* Command bar */}
      <div className="flex items-center gap-1 overflow-x-auto rounded-lg border bg-card px-2 py-1.5" data-testid="grid-commands">
        <Button variant="ghost" size="sm" onClick={() => navigate(`${route}/new`)} data-testid="grid-new">
          <Plus className="h-4 w-4" /> {tx("new")}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => list.refetch()} data-testid="grid-refresh">
          <RefreshCw className={`h-4 w-4 ${list.isFetching ? "animate-spin" : ""}`} /> {tx("refresh")}
        </Button>
        {selected.length > 0 && (
          <>
            <div className="mx-1 h-5 w-px shrink-0 bg-border" />
            {selected.length === 1 && (
              <Button variant="ghost" size="sm" onClick={sendDocument} data-testid="grid-send">
                <Send className="h-4 w-4" /> {tx("sendDocument")}
              </Button>
            )}
            {anyInactive && (
              <Button variant="ghost" size="sm" onClick={() => setState(true)} disabled={busy} data-testid="grid-activate">
                <Power className="h-4 w-4" /> {entity === "lead" ? tx("reopen") : tx("activate")}
              </Button>
            )}
            {anyActive && (
              <Button variant="ghost" size="sm" onClick={() => setState(false)} disabled={busy} data-testid="grid-deactivate">
                <PowerOff className="h-4 w-4" /> {tx("deactivate")}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setAssignTo(""); setAssignOpen(true); }} disabled={busy} data-testid="grid-assign">
              <UserCheck className="h-4 w-4" /> {tx("assign")}
            </Button>
            <Button variant="ghost" size="sm" onClick={removeSelected} disabled={busy} data-testid="grid-delete">
              <Trash2 className="h-4 w-4 text-destructive" /> {tx("delete")}
            </Button>
          </>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="grid-view-menu">
                <SlidersHorizontal className="h-4 w-4" /> <span className="hidden sm:inline">{tx("views")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onClick={() => setEditor(view?.canEdit ? "edit" : "saveAs")} data-testid="view-edit">
                {tx("editView")}
              </DropdownMenuItem>
              {dirty && view?.canEdit && (
                <DropdownMenuItem onClick={saveCurrent} data-testid="view-save-current">{tx("saveView")}</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setEditor("saveAs")} data-testid="view-save-as">{tx("saveAsView")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditor("create")}>{tx("createView")}</DropdownMenuItem>
              {view?.isSystem && view.overridden && view.canEdit && (
                <DropdownMenuItem onClick={resetSystemView} data-testid="view-reset">{tx("resetView")}</DropdownMenuItem>
              )}
              {view && !view.isSystem && view.canEdit && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={deleteView} className="text-destructive" data-testid="view-delete">{tx("deleteView")}</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-lg border bg-card">
        {views.isLoading || list.isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {tx("loading")}
          </div>
        ) : list.error ? (
          <p className="p-8 text-center text-sm text-destructive">{list.error.message}</p>
        ) : (
          <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth: 44 + columns.reduce((s, c) => s + c.width, 0) }}>
            <colgroup>
              <col style={{ width: 44 }} />
              {columns.map((c) => <col key={c.key} style={{ width: c.width }} />)}
            </colgroup>
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="px-3 py-2 text-left">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} aria-label={tx("selectAll")} data-testid="grid-select-all" />
                </th>
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  return (
                    <th key={c.key} ref={(el) => { thRefs.current[c.key] = el; }} className="relative select-none px-3 py-2 text-left font-medium">
                      <button type="button" onClick={() => toggleSort(c.key)} className="flex w-full items-center gap-1 truncate text-left"
                        title={active && sort?.dir === "asc" ? tx("sortDesc") : tx("sortAsc")} data-testid={`grid-col-${c.key}`}>
                        <span className="truncate">{fieldLabel(entity, c.key, lang)}</span>
                        {active && (sort?.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 shrink-0" /> : <ArrowDown className="h-3.5 w-3.5 shrink-0" />)}
                      </button>
                      <span onMouseDown={startResize(c.key)} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-ring/60"
                        aria-hidden />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-12 text-center text-muted-foreground" data-testid="grid-empty">
                    {tx("noRecords")}
                  </td>
                </tr>
              )}
              {pageRows.map((r) => {
                const isSel = selection.has(r.id);
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`${route}/${r.id}`)}
                    className={`cursor-pointer border-b last:border-b-0 hover:bg-muted/40 ${isSel ? "bg-accent/30" : ""} ${r.values.isActive === false ? "text-muted-foreground" : ""}`}
                    data-testid="grid-row"
                    data-id={r.id}
                  >
                    <td className="px-3 py-2" onClick={(e) => { e.stopPropagation(); toggleRow(r.id); }}>
                      <Checkbox checked={isSel} aria-label={tx("selectRow")} data-testid={`grid-select-${r.id}`} />
                    </td>
                    {columns.map((c, ci) => {
                      const text = c.key === "fullName" ? recordTitle(entity, r.values) : displayValue(entity, c.key, r, lang);
                      return (
                        <td key={c.key} className="truncate px-3 py-2" title={text}>
                          {ci === 0 ? <span className="font-medium text-secondary underline-offset-2 hover:underline">{text || "—"}</span> : text}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span data-testid="grid-count">
          {tx("records", { n: rows.length })}
          {selected.length > 0 ? ` · ${tx("selected", { n: selected.length })}` : ""}
        </span>
        {pages > 1 && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>{tx("prev")}</Button>
            <span>{tx("page", { p: page + 1, t: pages })}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>{tx("next")}</Button>
          </div>
        )}
      </div>

      {editor && (
        <CrmViewEditor
          entity={entity}
          open={!!editor}
          onOpenChange={(o) => { if (!o) setEditor(null); }}
          title={editor === "edit" ? tx("editView") : editor === "saveAs" ? tx("saveAsView") : tx("createView")}
          initial={currentDraft(editor)}
          isSystem={editor === "edit" && !!view?.isSystem}
          members={ctx.members}
          onSave={(d) => saveDraft(editor, d)}
        />
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md" data-testid="assign-dialog">
          <DialogHeader><DialogTitle>{tx("assignTitle", { n: selected.length })}</DialogTitle></DialogHeader>
          <FieldShell label={tx("newOwner")} required>
            <NativeSelect value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-owner">
              <option value="">{tx("select")}</option>
              {ctx.members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
            </NativeSelect>
          </FieldShell>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>{tx("cancel")}</Button>
            <Button onClick={assignSelected} disabled={!assignTo} data-testid="assign-confirm">{tx("assign")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
